import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Check } from 'react-feather';
import { FeatureTag } from '../ui/feature-tag';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { featureTagApi } from '../../services/api';
import './InlineTagEditor.css';

const POPOVER_OFFSET = 8;
const POPOVER_MIN_HEIGHT = 200;
const POPOVER_MAX_HEIGHT = 320;

const InlineTagEditor = ({ 
  task, 
  availableTags, 
  onTagUpdate,
  onTagCreate,
  projectId,
  className = "" 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, placement: 'bottom' });
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  // Smart suggestions based on task content
  const getSmartSuggestions = () => {
    const taskText = `${task.title} ${task.description || ''}`.toLowerCase();
    const suggestions = [];
    
    if (taskText.includes('login') || taskText.includes('auth') || taskText.includes('password')) {
      suggestions.push('Authentication');
    }
    if (taskText.includes('ui') || taskText.includes('frontend') || taskText.includes('design')) {
      suggestions.push('UI/UX');
    }
    if (taskText.includes('api') || taskText.includes('backend') || taskText.includes('server')) {
      suggestions.push('Backend');
    }
    if (taskText.includes('test') || taskText.includes('testing')) {
      suggestions.push('Testing');
    }
    if (taskText.includes('bug') || taskText.includes('fix') || taskText.includes('issue')) {
      suggestions.push('Bug Fix');
    }
    if (taskText.includes('database') || taskText.includes('db') || taskText.includes('sql')) {
      suggestions.push('Database');
    }
    
    return suggestions.filter(suggestion => 
      !availableTags.some(tag => tag.name.toLowerCase() === suggestion.toLowerCase())
    );
  };

  const smartSuggestions = getSmartSuggestions();

  // Calculate popover position - render in viewport via Portal
  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const popoverHeight = Math.min(POPOVER_MAX_HEIGHT, POPOVER_MIN_HEIGHT);
    
    // Prefer opening downward, but open upward if not enough space below
    const openUpward = spaceBelow < popoverHeight && spaceAbove > spaceBelow;
    const placement = openUpward ? 'top' : 'bottom';
    
    let top;
    if (placement === 'bottom') {
      top = rect.bottom + POPOVER_OFFSET;
    } else {
      top = rect.top - POPOVER_OFFSET; // Will use bottom positioning via CSS
    }
    
    // Horizontal: align to trigger left, but keep within viewport
    let left = rect.left;
    const popoverWidth = 320;
    if (left + popoverWidth > viewportWidth - 16) {
      left = viewportWidth - popoverWidth - 16;
    }
    if (left < 16) {
      left = 16;
    }
    
    setPopoverPosition({ top, left, placement, rect });
  }, []);

  useEffect(() => {
    if (isEditing) {
      updatePopoverPosition();
      // Reposition on scroll/resize
      const handleReposition = () => updatePopoverPosition();
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
      return () => {
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }
  }, [isEditing, updatePopoverPosition]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleClickOutside = useCallback((event) => {
    if (
      triggerRef.current &&
      !triggerRef.current.contains(event.target) &&
      !event.target.closest('.inline-tag-editor__popover')
    ) {
      setIsEditing(false);
      setNewTagName('');
    }
  }, []);

  useEffect(() => {
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isEditing, handleClickOutside]);

  const handleAddTag = async (tagId) => {
    try {
      if (task.feature_tag?.id === tagId) {
        setIsEditing(false);
        return;
      }
      
      if (onTagUpdate) {
        await onTagUpdate(task.id, tagId);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating task tag:', error);
      alert(error.response?.data?.error || 'Failed to update tag');
    }
  };

  const handleRemoveTag = async (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    try {
      if (onTagUpdate) {
        await onTagUpdate(task.id, null);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error removing tag from task:', error);
      alert(error.response?.data?.error || 'Failed to remove tag');
    }
  };

  const handleTagClick = () => {
    setIsEditing(true);
  };

  const handleCreateAndAddTag = async (tagName) => {
    try {
      setCreating(true);
      const newTag = await featureTagApi.create({
        name: tagName,
        project_id: projectId
      });

      if (onTagCreate) {
        onTagCreate(newTag);
      }

      if (onTagUpdate) {
        onTagUpdate(task.id, newTag.id);
      }

      setNewTagName('');
      setIsEditing(false);
    } catch (error) {
      console.error('Error creating and adding tag:', error);
      alert(error.response?.data?.error || 'Failed to create tag');
    } finally {
      setCreating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newTagName.trim()) {
      await handleCreateAndAddTag(newTagName.trim());
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    await handleCreateAndAddTag(suggestion);
  };

  const popoverContent = isEditing && (
    <div
      className={`inline-tag-editor__popover inline-tag-editor__popover--${popoverPosition.placement}`}
      style={{
        position: 'fixed',
        left: popoverPosition.left,
        top: popoverPosition.placement === 'bottom' 
          ? popoverPosition.top 
          : (popoverPosition.rect?.top ?? popoverPosition.top) - POPOVER_OFFSET,
        transform: popoverPosition.placement === 'top' ? 'translateY(-100%)' : undefined,
        zIndex: 10000,
      }}
    >
      <div className="inline-tag-editor__popover-inner">
        {/* Available Tags - scrollable */}
        {availableTags.length > 0 && (
          <div className="inline-tag-editor__section">
            <div className="inline-tag-editor__section-label">Available tags</div>
            <div className="inline-tag-editor__tag-list-scroll">
              {availableTags.map(tag => {
                const isCurrentTag = task.feature_tag?.id === tag.id;
                return (
                  <button
                    key={tag.id}
                    className={`inline-tag-editor__tag-option ${isCurrentTag ? 'inline-tag-editor__tag-option--current' : ''}`}
                    onClick={() => handleAddTag(tag.id)}
                    title={isCurrentTag ? 'Current tag (click to keep)' : 'Click to select this tag'}
                  >
                    <FeatureTag
                      name={tag.name}
                      color={tag.color}
                      size="small"
                    />
                    {isCurrentTag && (
                      <span className="inline-tag-editor__current-indicator">Current</span>
                    )}
                  </button>
                );
              })}
            </div>
            {task.feature_tag && (
              <button
                className="inline-tag-editor__remove-button"
                onClick={handleRemoveTag}
                title="Remove current tag"
              >
                <X size={14} />
                Remove tag
              </button>
            )}
          </div>
        )}

        {/* Smart Suggestions */}
        {smartSuggestions.length > 0 && (
          <div className="inline-tag-editor__section">
            <div className="inline-tag-editor__section-label">Suggested</div>
            <div className="inline-tag-editor__suggestion-list">
              {smartSuggestions.map(suggestion => (
                <button
                  key={suggestion}
                  className="inline-tag-editor__suggestion"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Create New Tag */}
        <form onSubmit={handleSubmit} className="inline-tag-editor__create-form">
          <div className="inline-tag-editor__section-label">Create new</div>
          <div className="inline-tag-editor__create-input">
            <TextField>
              <TextField.Input
                ref={inputRef}
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Tag name..."
                maxLength={50}
              />
            </TextField>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!newTagName.trim() || creating}
              icon={<Check size={14} />}
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>

        <button
          className="inline-tag-editor__cancel"
          onClick={() => {
            setIsEditing(false);
            setNewTagName('');
          }}
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div 
        className={`inline-tag-editor ${isEditing ? 'editing' : ''} ${className}`} 
        ref={triggerRef}
      >
        <div className="inline-tag-editor__tags">
          {task.feature_tag && (
            <div className="inline-tag-editor__current-tag-wrapper">
              <FeatureTag
                name={task.feature_tag.name}
                color={task.feature_tag.color}
                size="small"
                removable
                onClick={handleTagClick}
                onRemove={handleRemoveTag}
                className="inline-tag-editor__current-tag"
              />
            </div>
          )}
          
          {!task.feature_tag && (
            <button
              className="inline-tag-editor__add-button"
              onClick={() => setIsEditing(true)}
              title="Add feature tag"
            >
              <Plus size={12} />
              <span>Add tag</span>
            </button>
          )}
        </div>
      </div>

      {isEditing && createPortal(
        <>
          <div 
            className="inline-tag-editor__backdrop" 
            aria-hidden="true"
            onClick={() => {
              setIsEditing(false);
              setNewTagName('');
            }}
          />
          {popoverContent}
        </>,
        document.body
      )}
    </>
  );
};

export default InlineTagEditor;
